import React, { useMemo } from 'react'
import { FlowGrid } from '@fs/zion-ui'
import { css } from '@emotion/core'
import { useSlsStateContext } from '@fs/zion-sls'
import { VIEW_FORMAT } from './panels/PreferencesPanel'
import HistoryChangeCard from './HistoryChangeCard'
import HistoryChangeList from './historyChangeList'
import { useR2StateContext } from '../../../providers/r2Provider/R2Context'
import { findParentEventType } from '../../../utils/historyUtils'
import { toTitleCase } from '../../../utils/stringUtils'

const containerCss = css`
  margin-top: 1.875rem;
`

const PersonChangesHistory = ({ filter, historyChanges, title, expanded }) => {
  const { historyFormat } = useR2StateContext()
  const slsState = useSlsStateContext()

  // Cache event type lookups to avoid redundant recursive searches
  const eventTypeCache = useMemo(() => {
    const cache = {}
    historyChanges.forEach((change) => {
      // Only look up if not already in action data
      const hasEventType = change?.typeData?.eventType || change?.elementArg?.eventType
      if (!hasEventType && change.id && change.partitionKey) {
        cache[change.id] = findParentEventType(change.id, change.partitionKey, slsState)
      }
    })
    return cache
  }, [historyChanges, slsState])

  const historyCategories = useMemo(() => {
    return Array.from(
      new Map(
        historyChanges.map((change) => {
          const changeType =
            change?.typeData?.fieldType ||
            change?.typeData?.eventType ||
            change?.typeData?.relType ||
            change?.typeData?.recordType ||
            change?.typeData?.elementType

          // Use parent event type to distinguish date fields
          // For field-level changes, use cached parent event lookup
          let eventType = change?.typeData?.eventType || change?.elementArg?.eventType

          // If no eventType in action data, use cached lookup
          if (!eventType && change.id) {
            eventType = eventTypeCache[change.id]
          }

          const compositeKey = eventType ? `${eventType}::${changeType}` : changeType

          // Enhance label with event type context for fields within events
          const enhancedLabel =
            eventType && (change.displayLabel === 'Date' || change.displayLabel === 'Place')
              ? `${toTitleCase(eventType)} ${change.displayLabel}` // e.g., "Birth Date", "Birth Place"
              : change.displayLabel

          return [compositeKey, { type: compositeKey, label: enhancedLabel }]
        })
      ).values()
    )
  }, [historyChanges, eventTypeCache])

  const groupedChanges = useMemo(() => {
    if (!Array.isArray(historyChanges) || historyCategories === null) {
      return {}
    }

    const groups = {}

    historyCategories.forEach((category) => {
      const changesByCategory = historyChanges.filter((change) => {
        const changeType =
          change?.typeData?.fieldType ||
          change?.typeData?.eventType ||
          change?.typeData?.relType ||
          change?.typeData?.recordType ||
          change?.typeData?.elementType

        // Match using composite key with event type
        let eventType = change?.typeData?.eventType || change?.elementArg?.eventType

        // If no eventType in action data, use cached lookup
        if (!eventType && change.id) {
          eventType = eventTypeCache[change.id]
        }

        const changeCompositeKey = eventType ? `${eventType}::${changeType}` : changeType
        return changeCompositeKey === category.type
      })

      groups[category.type] = { label: category.label, items: changesByCategory }
    })

    return groups
  }, [historyChanges, historyCategories, eventTypeCache])

  const hasGroupedChanges = Object.keys(groupedChanges).length > 0

  return (
    <>
      {historyFormat === VIEW_FORMAT.group ? (
        <div css={containerCss}>
          <FlowGrid orientation="vertical">
            {hasGroupedChanges ? (
              Object.keys(groupedChanges).map((id) => (
                <HistoryChangeCard
                  key={id}
                  label={groupedChanges[id].label}
                  change={groupedChanges[id]}
                  expanded={expanded}
                />
              ))
            ) : (
              <HistoryChangeCard label={filter.label} change={{ items: [] }} expanded={expanded} />
            )}
          </FlowGrid>
        </div>
      ) : (
        <HistoryChangeList changes={historyChanges} title={title} />
      )}
    </>
  )
}

export default PersonChangesHistory
